import firstupImg from "../assets/img/firstup-dashboard.png";
import firstupJourneysImg from "../assets/img/firstup-journeys.png";
import firstupEngagementImg from "../assets/img/firstup-engagement.png";
import gosearchImg from "../assets/img/gosearch-ai.png";
import gosearchSearchImg from "../assets/img/gosearch-search.png";
import gosearchWorkflowsImg from "../assets/img/gosearch-workflows.png";
import gosearchIosHome from "../assets/img/gosearch-ios/ui/home.png";
import gosearchIosSearch from "../assets/img/gosearch-ios/ui/search.png";
import gosearchIosChat from "../assets/img/gosearch-ios/ui/chat.png";
import appleImg from "../assets/img/apple-store.png";

export const projects = [
  {
    id: "gosearch",
    title: "GoSearch AI",
    subtitle: "If it can’t cite, it doesn’t ship. 60%+ faster finding, with a paper trail.",
    img: gosearchImg,
    alt: "GoSearch AI using Jira and Slack to prioritize bug tickets and export a spreadsheet",
    tone: "navy",
    size: "wide",
    fit: "contain",
    tagline: "Search people can cite",
    intro:
      "Sole PM for GoSearch AI, owning strategy, roadmap, customer discovery, monetization, and launch across enterprise search, retrieval, evaluation, and agentic workflows.",
    role: "Technical Product Manager, AI",
    client: "GoLinks",
    shipped: "Jun 2025–Present",
    platform: "Enterprise search · RAG · agents",
    next: "gosearch-apps",
    slides: [
      {
        kicker: "Overview",
        title: "AI answers with a paper trail, not a vibe",
        body: "Teams were losing hours hunting across Drive, Confluence, Slack, Jira, and Salesforce. I took GoSearch AI Answers from a concept to a product people actually trust enough to use in front of a customer.",
        img: gosearchSearchImg,
        alt: "GoSearch unified results across Notion, Drive, and Q&A",
      },
      {
        kicker: "The problem",
        title: "The answer existed. The path to it did not.",
        body: "Knowledge was scattered, uncited, and stale. Generic chat wrappers looked impressive in a demo and fell apart the moment someone asked “where did this come from?” Enterprise search had to be right, sourced, and fast, or it would not get a second query.",
        rows: [
          { key: "Scattered systems", sub: "Five tools, one question", desc: "The same fact lived in Drive, Confluence, Slack, Jira, and Salesforce, and nobody knew which copy was current." },
          { key: "No citations", sub: "Trust gap", desc: "Answers without sources are a liability in enterprise. People need the document, the owner, and the timestamp." },
          { key: "Demo-grade AI", sub: "Not production-grade", desc: "Pretty chat is cheap. Evaluation, retrieval, and a path from “wrong” to “fixed” is the actual product." },
        ],
      },
      {
        kicker: "What shipped",
        title: "Answers, eval, and agents, in that order",
        body: "We launched AI Answers across the tools people already work in, then built the loops that make those answers better every week, then shipped read/write workflows so search could actually do work.",
        img: gosearchImg,
        alt: "GoSearch agent using Jira and Slack to build a bug-priority spreadsheet",
        rows: [
          { key: "AI Answers", sub: "0-to-1 launch", desc: "Cited answers across Drive, Confluence, Slack, Jira, and Salesforce." },
          { key: "Eval loops", sub: "Quality you can measure", desc: "LLM evaluation and feedback that improved answer quality 25%." },
          { key: "Agentic workflows", sub: "Function calling + MCP", desc: "Read/write agents that turn a question into an action, not another tab." },
        ],
      },
      {
        kicker: "How it works",
        title: "Retrieval is the product. The model is a guest.",
        body: "Hybrid retrieval, reranking, confidence thresholds, and human-in-the-loop eval. A/B tests on the unglamorous bits, because that is where accuracy actually moves.",
        img: gosearchWorkflowsImg,
        alt: "GoSearch workflow builder chaining Jira, Notion, and Slack",
      },
      {
        kicker: "The call",
        title: "If it can’t cite, it doesn’t ship.",
        body: "The demo everyone asked for was an open chat box. I held the launch until an answer could show the document, the owner, and the timestamp, and kept it quiet when retrieval was not sure enough to stand in front of a customer.",
        rows: [
          { key: "No naked answers", sub: "The trust rule", desc: "An answer without a source, an owner, and a timestamp never reached a customer." },
          { key: "Quiet when unsure", sub: "Confidence gate", desc: "Below the bar, the product asked a sharper question instead of guessing." },
          { key: "Eval, then agents", sub: "Order of operations", desc: "Read/write agents shipped only after the answer-quality loop was already moving." },
        ],
      },
      {
        kicker: "Outcome",
        title: "Faster to find. Safer to trust. Real revenue.",
        body: "The work shows up in time saved, answers people will actually use, and a freemium motion that paid for itself.",
        stats: [
          { value: "60%+", label: "faster information finding" },
          { value: "30%", label: "response accuracy lift" },
          { value: "$1M+", label: "incremental freemium revenue" },
        ],
        quote: "If it can’t cite the source, it doesn’t ship. That was the launch bar.",
      },
    ],
  },
  {
    id: "gosearch-apps",
    title: "iOS & Android",
    subtitle: "Rebuilt the GoSearch app people carry. Shipped 4.0 with no engineering team.",
    img: gosearchIosChat,
    alt: "GoSearch iPhone app showing a GoAI answer linked to a source document",
    tone: "navy",
    size: "narrow",
    fit: "phone",
    tagline: "The product left the laptop.",
    intro:
      "GoSearch died in the hallway. I rebuilt it for iPhone, iPad, and Android so the app people carry can search the company, answer with a source, and run an agent. I shipped 4.0 on 14 October 2026 with no engineering team.",
    role: "Sole builder",
    client: "GoLinks",
    shipped: "4.0 · 14 Oct 2026",
    platform: "iOS · iPad · Android",
    link: "https://apps.apple.com/co/app/gosearch-ai/id6476885744?l=en-GB",
    linkLabel: "View on the App Store",
    next: "gosearch-mcp",
    slides: [
      {
        kicker: "Overview",
        title: "The app people open",
        body: "The laptop was the real product. The phone was the leftover. 4.0 made the app in their pocket the same GoSearch: search, a sourced answer, and an agent.",
        img: gosearchIosChat,
        alt: "GoSearch iPhone app showing a GoAI answer linked to a Notion policy",
      },
      {
        kicker: "The constraint",
        title: "A team’s year. No team.",
        body: "Replacing the iOS app, putting it on iPad, and shipping Android is how companies staff a mobile org. There wasn’t one. Waiting would have left every person away from their desk on the old GoSearch.",
        rows: [
          { key: "Three clients", sub: "The scope", desc: "iPhone, iPad, and Android. The whole app on each, in one revamp." },
          { key: "No one to hand it to", sub: "The constraint", desc: "I didn’t hire an engineering team, and I didn’t park the redesign until one showed up." },
          { key: "The hallway", sub: "What was at stake", desc: "If the phone is the weak client, people learn that GoSearch only works at a desk." },
        ],
      },
      {
        kicker: "What shipped",
        title: "4.0 replaced the app.",
        body: "The build on the App Store is a different product. From a phone, someone can ask across the company’s tools, get an answer that names its source, start an agent, and search.",
        img: gosearchIosSearch,
        alt: "GoSearch iPhone search for quarterly goals, filtered by app and owner",
        rows: [
          { key: "Answers with sources", sub: "GoAI", desc: "A question across connected apps comes back with the document it came from." },
          { key: "Agents", sub: "In the app", desc: "Any agent the team has built can be started from the phone, against the apps you choose." },
          { key: "Search", sub: "The company, filtered", desc: "Documents, messages, and go links, narrowed by app, type, or owner." },
        ],
      },
      {
        kicker: "The call",
        title: "A phone that can’t cite trains people not to trust it.",
        body: "A chat box would have shipped faster and taught the company that mobile GoSearch is a toy. I held 4.0 to the web bar: an answer shows its source, or it doesn’t ship. Then I shipped that, alone.",
        img: gosearchIosHome,
        alt: "GoSearch iPhone home with shortcuts and recently used work links",
        rows: [
          { key: "Source on screen", sub: "The bar", desc: "The answer links the document. The smaller screen was not a reason to drop it." },
          { key: "Choose the apps", sub: "Control", desc: "GoAI searches the apps you pick, so the phone answers from the work that matters." },
          { key: "A chat you return to", sub: "It has to last", desc: "Pin, rename, or delete a thread. 4.0 is a tool people reopen, not a demo they try once." },
        ],
      },
      {
        kicker: "Outcome",
        title: "The version people download is the redesign.",
        body: "On 14 October 2026 the App Store replaced the old GoSearch app with 4.0. iPhone and iPad. Android in the same revamp. One person. No engineering team behind it.",
        rows: [
          { key: "4.0", sub: "In market", desc: "A complete redesign on iPhone and iPad, live on the App Store." },
          { key: "Android", sub: "Same revamp", desc: "The other phone got the product in the same effort." },
          { key: "The team", sub: "Me", desc: "No engineering hires. The apps shipped because I built them." },
        ],
        quote: "I replaced the GoSearch app on iPhone, iPad, and Android without an engineering team.",
      },
    ],
  },
  {
    id: "gosearch-mcp",
    title: "Connectors & MCP",
    subtitle: "Led 100+ integrations, and GoSearch MCP inside Claude.",
    img: gosearchWorkflowsImg,
    alt: "GoSearch workflow builder chaining Jira, Notion, and Slack",
    tone: "navy",
    size: "wide",
    fit: "contain",
    tagline: "If the agent can’t reach the tool, it isn’t a product.",
    intro:
      "I led the GoSearch integrations catalog, and the partnership that listed GoSearch in the Claude app directory. The public page is 100+ apps, indexed, federated, or MCP, and Claude can use that company knowledge while someone codes, prototypes, or makes an artifact.",
    role: "Technical Product Manager, AI",
    client: "GoLinks",
    shipped: "Live catalog",
    platform: "Indexed · Federated · MCP",
    link: "https://www.gosearch.ai/integrations",
    linkLabel: "See the integrations",
    partnerLink: "https://www.gosearch.ai/blog/gosearch-mcp-in-claude/",
    partnerLabel: "GoSearch in Claude",
    next: "data-platform",
    slides: [
      {
        kicker: "Overview",
        title: "The library is the product",
        body: "Search that stops at one app is a demo. I treated the integrations page as the roadmap: indexed where we sync it, federated where the data stays in the source, and MCP where an agent has to call the tool.",
        img: gosearchWorkflowsImg,
        alt: "GoSearch workflow builder chaining Jira, Notion, and Slack",
      },
      {
        kicker: "The call",
        title: "Three ways in. One library.",
        body: "The integrations page does not force one method. I ran all three, and I held each to the same rule: the source keeps its permissions, and the data is not a copy we have to apologize for.",
        rows: [
          { key: "Indexed", sub: "Sync it", desc: "Customizable indexing with automatic data syncing. The bulk of the native catalog." },
          { key: "Federated", sub: "Don’t copy it", desc: "Real-time fetch. Nothing duplicated. Drive, Slack, Jira, Notion, GitHub, Salesforce, and the rest of that set." },
          { key: "MCP", sub: "Call the tool", desc: "Official servers, GoSearch MCP, and a custom connector. Personal setup can be a native app, an official MCP server, or your own." },
        ],
      },
      {
        kicker: "What shipped",
        title: "The library got bigger because the roadmap did.",
        body: "The integrations page is the list. Native apps stayed, and MCP landed on top of the ones that needed an agent, plus a run of MCP-only tools that were never a native connector.",
        rows: [
          { key: "Native and MCP", sub: "Both paths", desc: "Notion, Linear, Asana, Box, GitHub, Salesforce, Miro, Highspot, HubSpot, Dropbox, and the rest of that set. Search the app, and call it." },
          { key: "MCP only", sub: "The increase", desc: "Amplitude, Attio, Datadog, Granola, Intercom, Mobbin, Monday, PostHog, Ramp, Smartsheet, and others that exist on the page as MCP." },
          { key: "Twelve categories", sub: "One hub", desc: "Docs, messaging, code, CRM, people data, task management, design, calendar, IT, operations, collaboration, and the Go suite." },
        ],
      },
      {
        kicker: "Who it’s for",
        title: "A workspace, or one person.",
        body: "The same catalog serves both. A workspace admin turns connectors on for the company, with groups and permissions. A person can add a source for themselves: a GoSearch native connector, an official MCP server, or their own custom MCP server, retrieved in real time and never stored as a second copy.",
        rows: [
          { key: "Workspace", sub: "The company", desc: "Groups, permissions, and the connectors the whole org is allowed to search." },
          { key: "Personal", sub: "One seat", desc: "Native, official MCP, or a custom MCP server. Live read. No duplicate store." },
          { key: "Not on the list", sub: "Custom MCP", desc: "The page’s third MCP path: bring a server we have not shipped, and it sits beside the catalog." },
        ],
      },
      {
        kicker: "Claude",
        title: "Company knowledge, inside Claude.",
        body: "I led the partnership that put GoSearch in the Claude app directory on 10 September 2026. The GoSearch MCP connection brings a person’s company knowledge into Claude. GoSearch stays the place to search and act. Claude is another place that knowledge can go.",
        rows: [
          { key: "App directory", sub: "10 Sep 2026", desc: "Listed in the Claude app directory. Connect, authorize, and Claude can search the GoSearch sources that account can already open." },
          { key: "Same access", sub: "Permission-aware", desc: "Claude reaches the apps already connected to GoSearch (docs, chat, code, CRM, project tools) under the access that person already has." },
          { key: "The work", sub: "Code and prototype", desc: "A developer builds from internal docs and patterns. A PM prototypes from process and customer history. High-stakes facts still get checked against the source." },
        ],
        link: "https://www.gosearch.ai/blog/gosearch-mcp-in-claude/",
        linkLabel: "Read the Claude post",
      },
      {
        kicker: "Outcome",
        title: "The integrations page is the number.",
        body: "I led the catalog: 100+ apps, 33 with MCP, 25 federated, 67 indexed, and the partnership that put that knowledge inside Claude.",
        stats: [
          { value: "100+", label: "apps on the integrations page" },
          { value: "33", label: "with an MCP connector" },
          { value: "25", label: "federated, data not copied" },
        ],
        quote: "I grew the integrations page on purpose, then took GoSearch itself into Claude so the knowledge does not stop at our product.",
      },
    ],
  },
  {
    id: "data-platform",
    title: "Data platform",
    subtitle: "Shipped APIs, dashboards, and platform features with a 98% roadmap hit rate.",
    img: firstupImg,
    alt: "Firstup Insights executive dashboard with engagement funnel",
    tone: "lilac",
    size: "narrow",
    fit: "cover",
    tagline: "One path for 250+ enterprises",
    intro:
      "Owned data-platform products spanning APIs, analytics pipelines, and dashboards for Fortune 100 customers, including the unglamorous work that makes the tenth customer as clean as the first.",
    role: "Product Manager, Data",
    client: "Firstup",
    shipped: "Mar 2022–May 2025",
    platform: "APIs · dashboards · compliance",
    next: "analytics",
    slides: [
      {
        kicker: "Overview",
        title: "A platform that could survive Fortune 50 constraints",
        body: "Firstup’s data platform had to onboard U.S. and E.U. enterprises onto one set of APIs and dashboards without inventing a custom path for every legal team. I owned that product end to end.",
        img: firstupImg,
        alt: "Firstup Insights executive dashboard with engagement funnel",
      },
      {
        kicker: "The problem",
        title: "Every new client meant a new integration story",
        body: "Lead time was the product. Sensitive geospatial and compliance constraints turned each onboarding into a one-off, and the dashboard layer lagged behind the data we already had.",
        rows: [
          { key: "Custom onboarding", sub: "Does not scale", desc: "Each enterprise arrived with a different stack, a different legal review, and a different definition of done." },
          { key: "Compliance", sub: "U.S. and E.U.", desc: "Geospatial and data-residency constraints could not be a footnote. They had to be a default path." },
          { key: "Insight lag", sub: "Data without a home", desc: "Pipelines existed. The surfaces executives would actually reopen did not." },
        ],
      },
      {
        kicker: "What shipped",
        title: "APIs, pipelines, and dashboards on one roadmap",
        body: "I treated onboarding as a product: a repeatable path through APIs, analytics pipelines, and Insights dashboards, with a 98% strategic roadmap hit rate.",
        img: firstupEngagementImg,
        alt: "Firstup engagement metrics for compliance and training initiatives",
      },
      {
        kicker: "Outcome",
        title: "The tenth customer used the same path as the first",
        body: "That is the only scale that counts, including the messy geospatial and compliance constraints.",
        stats: [
          { value: "250+", label: "enterprise clients onboarded" },
          { value: "70%", label: "faster integrations" },
          { value: "98%", label: "roadmap hit rate" },
        ],
        quote: "Scale only counts when the tenth customer has the same path as the first, including the messy geospatial and compliance constraints.",
      },
    ],
  },
  {
    id: "analytics",
    title: "Predictive analytics",
    subtitle: "Launched two analytics apps from scratch, reducing operational costs by $1M+.",
    img: firstupJourneysImg,
    alt: "Firstup Journey Insights dashboard with channel performance and engagement trends",
    tone: "lilac",
    size: "narrow",
    fit: "cover",
    tagline: "Two apps. No dedicated eng team.",
    intro:
      "Launched two predictive analytics applications from 0-to-1 with no dedicated engineering team, then put them in the hands of 15+ Fortune 100 companies.",
    role: "Product Manager, Data",
    client: "Firstup",
    shipped: "0-to-1 · five countries",
    platform: "Journey Insights · predictive apps",
    next: "apple",
    slides: [
      {
        kicker: "Overview",
        title: "Analytics that showed up as a line item, not a slide",
        body: "The brief was not another dashboard. It was: cut operating cost, cut labor, and make journey performance something operators could act on without a data team in the room.",
        img: firstupJourneysImg,
        alt: "Firstup Journey Insights dashboard with channel performance and engagement trends",
      },
      {
        kicker: "The constraint",
        title: "Ship it anyway",
        body: "No dedicated engineering team. Fortune 100 buyers. Five countries. The only way through was a thin slice, ruthless scope, and a product that paid for itself in labor saved.",
        rows: [
          { key: "0-to-1", sub: "Two applications", desc: "Predictive analytics apps built from scratch, not a reskin of an existing report." },
          { key: "No dedicated eng", sub: "Builder’s bias", desc: "Scope had to be small enough to ship and sharp enough that operators would keep it open." },
          { key: "Global", sub: "Five countries", desc: "The same app had to work across 15+ Fortune 100s without a local rewrite." },
        ],
      },
      {
        kicker: "What shipped",
        title: "Journey performance you can read in one sitting",
        body: "Channel performance, engagement trends, and the funnel operators already understood, instrumented so a manager could see what moved, not just what launched.",
        img: firstupJourneysImg,
        alt: "Journey Insights with channel performance and weekly engagement trends",
      },
      {
        kicker: "Outcome",
        title: "A P&L change, not a deck metric",
        body: "Two analytics apps, 90% less labor, and more than a million dollars back in operating cost.",
        stats: [
          { value: "$1M+", label: "operating cost reduced" },
          { value: "90%", label: "less labor" },
          { value: "15+", label: "Fortune 100 companies" },
        ],
        quote: "Two analytics apps shipped with no dedicated engineering team. A line item on the P&L, not a slide metric.",
      },
    ],
  },
  {
    id: "apple",
    title: "Apple Online Store",
    subtitle: "Store features that lifted engagement 15% and online sales 20%.",
    img: appleImg,
    alt: "Apple.com storefront featuring iPhone 18 Pro",
    tone: "ink",
    size: "wide",
    fit: "cover",
    tagline: "Craft at commercial scale",
    intro:
      "Shipped Apple Online Store features and a real-time monitoring platform used at commercial scale, via Infosys. The unglamorous path from a storefront idea to something that holds up on a peak traffic day.",
    role: "Application Engineer",
    client: "Apple · via Infosys",
    shipped: "May 2021–Mar 2022",
    platform: "Storefront · campaign monitoring",
    next: "gosearch",
    slides: [
      {
        kicker: "Overview",
        title: "Storefront features that had to work on the biggest stage",
        body: "Apple.com is not a place you get to “iterate in production.” I shipped storefront features and the monitoring underneath them so campaigns could run without becoming an engineering incident.",
        img: appleImg,
        alt: "Apple.com storefront featuring iPhone 18 Pro",
      },
      {
        kicker: "What shipped",
        title: "The feature and the nervous system",
        body: "Storefront work that customers actually touch, plus a data monitoring platform tracking 100k+ campaigns so engineering was not the pager for every anomaly.",
        rows: [
          { key: "Storefront", sub: "Customer-facing", desc: "Features that increased engagement 15% and contributed to a 20% lift in online sales." },
          { key: "Monitoring", sub: "100k+ campaigns", desc: "A real-time platform that made campaign health visible before it became a ticket." },
          { key: "Support load", sub: "The quiet win", desc: "Engineering support time down 70%, support tickets down 10%." },
        ],
      },
      {
        kicker: "Outcome",
        title: "Engagement up. Sales up. Noise down.",
        body: "The work had to show up twice: in the store, and in the hours engineering got back.",
        stats: [
          { value: "15%", label: "engagement lift" },
          { value: "20%", label: "online sales lift" },
          { value: "70%", label: "less engineering support time" },
        ],
        quote: "Shipped storefront features people could tap, and a monitoring layer so 100k+ campaigns did not turn into 100k tickets.",
      },
    ],
  },
];

export const projectById = Object.fromEntries(projects.map((project) => [project.id, project]));
